import { Grid } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { CTBox, StyledGrid} from "../style/StyledComponents";
import TimelineBar from "../MainVideoPage/TimelineBar";
//import './PodcastPlayerStyle.css';
import ControlButtons from "../MainVideoPage/ControlButtons";
import BookmarkComponent from "../MainVideoPage/VideoBookmark";
import { getSectionForIndex, insertSection, StringToBookmarks } from "../MainVideoPage/bookmarks";
import { initialBookmark } from "../utils/Constants";
import { useLocation, useParams } from "react-router-dom";
import PodcastBanner from "./PodcastBanner";
import { getAllSavedNotes, savePodcastBookmarksInNotes } from "../../storage/PodcastLocalStorage";
import { encode as base64_encode } from 'base-64';
import { base64UrlDecode, extractPodcastMetadata, getPodcastKey, makePodcastObject } from "./podcastUtils";
import PodcastExportBoard from "./PodcastExportBoard";
import { exportInClipThoughtsFormat } from "./podcastExportUtils";
import QuickMarkBoard from "../MainVideoPage/QuickMarkBoard";

const PodcastPlayer = () => {

  /* UseParams get the encoded podcastId and encodedMp3 fromt the url */
  const {encodedRssFeed, encodedMp3, /*bookmark*/} = useParams();
  const [rssFeed, setRssFeed] = useState("");

  const location = useLocation();
  const [podcastMetadata, setPodcastMetadata] = useState(location.state || {});
  const [mp3, setMp3] = useState(encodedMp3 || "");

  // player related state
  const playerRef = useRef(null);
  const [player, setPlayer] = useState();

  // annotation/bookmarks releated state
  const [bookmarks, setBookmarks] = useState([{...initialBookmark}]);
  const [tabValue, setTabValue] = React.useState(0);

  // Export state
  const [openExportDialog, setOpenExportDialog] = useState(false);

  const gridRef = useRef();

  // Loop related state
  const [loop, setLoop] = React.useState([]);
  const [loopButtonClick, setLoopButton] = React.useState(false);
  const [timeoutId, setTimeoutId] = React.useState();

  // Refs are needed to keep these values up to date.
  const encodedRssFeedRef = useRef(encodedRssFeed);
  const encodedMp3Ref = useRef(encodedMp3);
  const podcastMetadataRef = useRef(podcastMetadata);
  const bookmarksRef = useRef(bookmarks);
  
  const [timeStampsText, setTimeStampsText] = useState("");
  const [openQuickMarksDialog, setOpenQuickMarksDialog] = useState(false);

  const handleStringToBookmarks = (event) => {
      StringToBookmarks(
        timeStampsText,
        bookmarks,
        setBookmarks
      )
  }
  /*
  * Runs whenever a component mounts. It decodes encodedMp3 and podcastId(id) from the url and sets the state
  * with decodedUrl.
  */
  useEffect(() => {
    const mp3 = base64UrlDecode(encodedMp3);
    const rssFeed = base64UrlDecode(encodedRssFeed);
    setMp3(mp3);
    setRssFeed(rssFeed);
    // Commenting the bookmarks part for now, as we currently are not supporting bookmarks
    /*if (bookmark) {
      const decodedBookmark = base64UrlDecode(bookmark);
      if (decodedBookmark && decodedBookmark.bookmarks) {
        //setPodcastMetadata(state);
        setBookmarks(decodedBookmark.bookmark);
      }
    }*/
  }, [encodedRssFeed, encodedMp3, /*bookmark*/]);


  // Component start code
  useEffect(() => {
    
    const handleBeforeUnload = (event) => {
      const encodedRssFeed = encodedRssFeedRef.current;
      const encodedMp3 = encodedMp3Ref.current;
      const bookmarks = bookmarksRef.current;
      const podcastMetadata = podcastMetadataRef.current;
      if (encodedRssFeed && encodedMp3 && bookmarks) {
        const podcastDetails = {
          ...podcastMetadata,
          updateTime: Date.now(),
          bookmarks: bookmarks.slice(0, -1)
        };
        
        savePodcastBookmarksInNotes(getPodcastKey(encodedRssFeed, encodedMp3), podcastDetails);
      }
    };

    // Add event listener when component mounts
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Remove event listener when component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    if (encodedRssFeed && encodedMp3) {
      const notes = getAllSavedNotes();
      if (notes && notes[getPodcastKey(encodedRssFeed, encodedMp3)]) {
        const note = notes[getPodcastKey(encodedRssFeed, encodedMp3)]
        const bookmarks = notes[getPodcastKey(encodedRssFeed, encodedMp3)].bookmarks;
        const podcastMetadata = extractPodcastMetadata(note);
        setBookmarks(bookmarks);
        setPodcastMetadata(podcastMetadata);
      }
    }
    return () => {
      const encodedRssFeed = encodedRssFeedRef.current;
      const encodedMp3 = encodedMp3Ref.current;
      const bookmarks = bookmarksRef.current;
      const podcastMetadata = podcastMetadataRef.current;
      if (encodedRssFeed && encodedMp3 && bookmarks) {
        const podcastDetails = {
          ...podcastMetadata,
          updateTime: Date.now(),
          bookmarks: bookmarks.slice(0, -1),
        };
        savePodcastBookmarksInNotes(getPodcastKey(encodedRssFeed, encodedMp3), podcastDetails);
      }
    }
  }, [encodedRssFeed, encodedMp3]);

  // This useEffect is used to keep id, bookmarks and encodedMp3 updated. Because what happens at
  // the reload or when the back button is clicked, the destructor of above useEffect runs.
  // And when that runs it doesn't know the old id, encodedMp3 and bookmarks value.
  // I still need to research why this happens.
  useEffect(() => {
    encodedRssFeedRef.current = encodedRssFeed;
    bookmarksRef.current = bookmarks;
    encodedMp3Ref.current = encodedMp3;
    podcastMetadataRef.current = podcastMetadata;
  }, [encodedRssFeed, encodedMp3, bookmarks, podcastMetadata]);

  const handleReadyEvent = (e) => {
    setPlayer(e);
  }

  const handleBookmarkEvent = (e) => {
    const currentTime = Math.floor(playerRef.current.getCurrentTime());
    const index = insertSection(bookmarks, setBookmarks, currentTime);
    setTabValue(index);
  }

  // Timeline button related methods
  const handleTimelineClick = (sectionIndex) => {
    setTabValue(sectionIndex);
  }

  const handleTimelineDoubleClick = (sectionIndex) => {
    handleTimelineClick(sectionIndex);
    const startTimeForIndex = getSectionForIndex(bookmarks, sectionIndex).time;
    if (startTimeForIndex >= 0) {
      playerRef.current.seekTo(startTimeForIndex);
    }
  }

  // Player related callbacks
  const handleDuration = (duration) => {
    const endBookmark = {
      ...initialBookmark,
      time: duration,
    }
    const bk = [...bookmarks, endBookmark];
    setBookmarks(bk);
  }

  const handleStartClick = (i) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    } 
    if (playerRef.current) {
      playerRef.current.seekTo(bookmarks[i].time);
      playerRef.current.getInternalPlayer().pause();
      playerRef.current.getInternalPlayer().play();
    }
  }
  
  const restartVideoSection = () => {
    playerRef.current.seekTo(loop[0]);
    playerRef.current.getInternalPlayer().pause();
    playerRef.current.getInternalPlayer().play();
  }

  const handleLoopClick = (i) => {
    if (timeoutId) { // Clear current timer
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
    setLoop([bookmarks[i].time, bookmarks[i + 1].time]);
    setLoopButton(true);
    handleStartClick(i);
  }

  const handleUnloopEvent = (event) => {
    setLoop([]);
    setLoopButton(false);
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }

  const handlePlayEvent = (event) => {
    if (loopButtonClick) {
      const duration = (loop[1] - Math.floor(playerRef.current.getCurrentTime())) / 1;
      const max_duration = (loop[1] - loop[0]) / 1;
      if ((duration >= 0) && (duration <= max_duration)){
        const localTimeoutId = setTimeout(restartVideoSection, duration * 1000);
        setTimeoutId(localTimeoutId);
      } else {
        if (timeoutId) {
          clearTimeout(timeoutId);
          setTimeoutId(null);
        }
        setLoopButton(false);
      }
    }
  }

  const [shareLink, setShareLink] = useState();
  // share link
  const handleShareButtonClick = () => {    
  const encodedLink = base64_encode(
          JSON.stringify({
            ...podcastMetadata,
            bookmarks: bookmarks.slice(0, -1),
          })
      );
      const shareLink = `${window.location.origin}/podcasts/${encodedRssFeed}/encodedMp3/${encodedMp3}/bookmarks/${encodedLink}`;
      setShareLink(shareLink);
  };

  const handleOpenExportDialog = () => {
    setOpenExportDialog(true);
  };

  const handleSaveClipThoughtsJsonFormat = () => {
    exportInClipThoughtsFormat(makePodcastObject(encodedRssFeed, encodedMp3, bookmarks, podcastMetadata));
  };

  return (
      <>
    <CTBox>
      <StyledGrid container spacing={1} sx={{paddingTop: 2}}>
        <Grid item sx={{width: "1200px", maxWidth:"1200px"}}>
          {player && <PodcastBanner 
                      imageSource={podcastMetadata.image ? podcastMetadata.image : ""}
                      heading={podcastMetadata.title ? podcastMetadata.title : ""}
                      description={podcastMetadata.description ? podcastMetadata.description : ""}
                      />
          }
          <Grid item ref={gridRef} className="podcast-player-wrapper">
            {<ReactPlayer
              ref={playerRef}
              url={mp3}
              controls
              width='100%'
              height='40px'
              onReady={handleReadyEvent}
              onDuration={handleDuration}
              onPlay={handlePlayEvent}
              muted
              />}
          </Grid>
          {player && bookmarks && <TimelineBar
                                  bookmark={bookmarks}
                                  tabValue={tabValue}
                                  handleTimelineClick={handleTimelineClick}
                                  handleTimelineDoubleClick={handleTimelineDoubleClick}
                                  />
          }
          {player && bookmarks && <ControlButtons
            showShareUrl={false}
            handleBookmarkEvent={handleBookmarkEvent}
            loop={loop} 
            loopButtonClick={loopButtonClick}
            handleUnloopEvent={handleUnloopEvent}
            shareLink={shareLink}
            handleShareButtonClick={handleShareButtonClick}
            handleOpenExportDialog={handleOpenExportDialog}
            handleExportBookmarks={handleSaveClipThoughtsJsonFormat}
            setOpenQuickMarksDialog={setOpenQuickMarksDialog}
            />}
          {player && 
          <BookmarkComponent
            title={podcastMetadata.title}
            playerRef={playerRef}
            bookmark={bookmarks}
            setBookmark={setBookmarks}
            gridRef={gridRef}
            tabValue={tabValue}
            handleStartClick={handleStartClick}
            handleLoopClick={handleLoopClick}
            handleTimelineClick={handleTimelineClick}
            handleUnloopEvent={handleUnloopEvent}
          />
          }
        </Grid>
      </StyledGrid>
    </CTBox>
    <PodcastExportBoard
          openExportDialog={openExportDialog}
          setOpenExportDialog={setOpenExportDialog}
          podcast={makePodcastObject(encodedRssFeed, encodedMp3, bookmarks, podcastMetadata)}
    />
    <QuickMarkBoard 
      openQuickMarksDialog={openQuickMarksDialog}
      setOpenQuickMarksDialog={setOpenQuickMarksDialog}
      timeStampsText={timeStampsText}
      setTimeStampsText={setTimeStampsText}
      handleStringToBookmarks={handleStringToBookmarks}
    />
    </>
  );
}

export default PodcastPlayer;